import Axios from 'axios';
import { TutorSchedule, ScheduleRequest } from '@/modules/schedule';

interface CalendarRequest {
  calendarId: number;
}

interface SearchCondition {
  perPage: number;
  page: number;
  sort: string;
  date: Date | null;
}

interface Calendar {
  calendarId: number;
  year: number;
  month: number;
  isTemporary: boolean;
  schedules: TutorSchedule[];
}
const emptyCalendar = (): Calendar => {
  return {
    calendarId: 0,
    year: 0,
    month: 0,
    isTemporary: false,
    schedules: []
  };
};

interface CreateRequest {
  date: Date | null;
  isUseBaseSchedule: boolean;
}
const emptyCreateRequest = (): CreateRequest => {
  return {
    date: null,
    isUseBaseSchedule: true,
  };
};

// 型の Export
export {
  SearchCondition,
  Calendar,
  CreateRequest
};

interface State {
  isLoading: boolean;
  searchCondition: SearchCondition;
  list: Calendar[];
  info: Calendar;
  createRequest: CreateRequest;
}

const mapState = {
  isLoading: false,
  searchCondition: {
    perPage: 20,
    page: 1,
    sort: '',
    date: null,
  },
  list: [],
  info: emptyCalendar(),
  createRequest: emptyCreateRequest(),
};

const mapGetters = {
  getSchedule: (state: State) => (scheduleId: number) => {
    for (const tutorSchedule of state.info.schedules) {
      for (const schedule of tutorSchedule.schedules) {
        if (schedule.scheduleId === scheduleId) {
          return schedule;
        }
      }
    }
    return null;
  }
};

const mapMutations = {
  setIsLoading(state: State, isLoading: boolean) {
    state.isLoading = isLoading;
  },

  setList(state: State, list: Calendar[]) {
    state.list = list;
    state.isLoading = false;
  },

  setInfo(state: State, info: Calendar) {
    state.info = info;
    state.isLoading = false;
  },

  setCreateRequest(state: State, request: CreateRequest) {
    state.createRequest = request;
  }
};

const mapActions = {
  async getListRequest({ commit, state }: any) {
    commit('setIsLoading', true);
    const response = await Axios.post<Calendar[]>('/api/Calendar/Search', state.searchCondition).catch((error) => {
      commit('setList', []);
      throw error;
    });
    if (response) {
      commit('setList', response.data);
    }
  },

  async getInfoRequest({ commit }: any, calendarId: number) {
    commit('setIsLoading', true);
    const response = await Axios.post<Calendar>('/api/Calendar/Calendar', {
      calendarId,
    }).catch((error) => {
      commit('setInfo', emptyCalendar());
      throw error;
    });
    if (response) {
      await commit('setInfo', response.data);
    }
  },

  async resetCreateRequest({ commit }: any) {
    commit('setCreateRequest', emptyCreateRequest());
  },

  /**
   * 作成可能な日付かどうか api 側に問い合わせる
   *
   * @param {*} { commit, state }
   */
  async confirmedCreateDate({ commit, state }: any) {
    const response = await Axios.post<boolean>('/api/Calendar/ConfirmedCreateDate', state.createRequest)
      .catch((error) => {
        throw error;
      });
    if (response && response.data === false) {
      const request = JSON.parse(JSON.stringify(state.createRequest));
      request.date = null;
      commit('setCreateRequest', request);
      return false;
    }
    return true;
  },

  /**
   * 新規一時追加リクエスト
   *
   * @param {*} { commit, dispatch, state }
   */
  async createTemporoaryRequest({ commit, dispatch, state }: any) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Calendar/CreateTemporary', state.createRequest).catch((error) => {
      throw error;
    });
    commit('setIsLoading', false);
    if (response) {
      return response.data.calendarId;
    }
    return 0;
  },

  /**
   * 新規追加リクエスト
   *
   * @param {*} { commit, dispatch, state }
   */
  async createRequest({ commit }: any, calendarId: number) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Calendar/Create', {
      calendarId
    })
    .catch((error) => {
      throw error;
    });
    commit('setIsLoading', false);
    if (response) {
      return response.data.calendarId;
    }
    return 0;
  },

  /**
   * 削除リクエスト
   */
  async removeRequest({ commit }: any, calendarId: number) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Calendar/Remove', {
      calendarId
    })
    .catch((error) => {
      commit('setIsLoading', false);
      throw error;
    });
    commit('setIsLoading', false);
    return true;
  },

  async outputRequest({ commit }: any, calendarId: number) {
    commit('setIsLoading', true);
    const request: CalendarRequest = {
      calendarId
    };
    const response = await Axios.post('/api/Calendar/DownloadTutorWorkingHours',
      request,
      { responseType: 'blob' }
    )
    .catch((error) => {
      commit('setIsLoading', false);
      throw error;
    });
    commit('setIsLoading', false);

    let fileName = '';
    const disposition = response.headers['content-disposition'];
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (matches != null && matches[1]) {
        fileName = decodeURI(matches[1].replace(/['"]/g, ''));
      }
    }

    if (fileName === '') {
      fileName = '勤務時間.xlsx';
    }

    if (!(window.navigator as any).msSaveOrOpenBlob) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = (window.navigator as any).msSaveOrOpenBlob(new Blob([response.data]), fileName);
    }
  },

  async createScheduleRequest({ commit }: any, request: ScheduleRequest) {
    const response = await Axios.post('/api/Calendar/ScheduleCreate', request).catch((error) => {
      throw error;
    });
    if (!response) { return; }
    return response.data;
  },

  async editScheduleRequest({ commit }: any, request: ScheduleRequest) {
    const response = await Axios.post('/api/Calendar/ScheduleEdit', request).catch((error) => {
      throw error;
    });
    if (!response) { return; }
    return response.data;
  },

  async removeScheduleRequest({ commit }: any, request: {calendarId: number, scheduleId: number}) {
    const response = await Axios.post('/api/Calendar/ScheduleRemove', request).catch((error) => {
      throw error;
    });
    if (!response) { return false; }
    return true;
  },

  async copyScheduleRequest({ commit }: any, request: ScheduleRequest) {
    const response = await Axios.post('/api/Calendar/ScheduleCopy', request).catch((error) => {
      throw error;
    });
    if (!response) { return; }
    return response.data;
  },

  async addReserver(
    {commit}: any,
    request: {calendarId: number, scheduleId: number, studentId: string, subjectId: string}
  ) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Calendar/AddReserver', request);
    commit('setIsLoading', false);
    if (response) {
      return response.data;
    }
    return {message: '生徒の予約に失敗しました'};
  },

  async removeReserver({commit}: any, request: {scheduleId: number, studentId: string}) {
    commit('setIsLoading', true);
    const response = await Axios.post('/api/Calendar/DeleteReserver', request);
    commit('setIsLoading', false);
    if (response) {
      return response.data;
    }
    return {message: '生徒の予約に失敗しました'};
  },

};

// ストアの Export
export default {
  namespaced: true,
  state: mapState,
  getters: mapGetters,
  actions: mapActions,
  mutations: mapMutations,
};
